import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  Box,
  VStack,
  HStack,
  Badge,
  Divider,
  Spinner,
  Alert,
  AlertIcon,
  useDisclosure,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Textarea,
  Grid,
  GridItem,
  Card,
  CardBody,
  Icon,
  Flex,
  Spacer
} from '@chakra-ui/react';
import { getApi } from 'common/services/api';
import { toast } from 'react-toastify';
import { getStatusColor } from 'common/utils/statusColor';
import { CalendarIcon, TimeIcon, StarIcon, CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';

const PreviousApplicationInterviewModal = ({ 
  isOpen, 
  onClose, 
  applicationId, 
  candidateName,
  jobTitle,
  hasPermission = false,
  currentInterviewId = null,
  currentInterviewDate = null,
  currentRound = null
}) => {
  const [previousInterviews, setPreviousInterviews] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const { 
    isOpen: isPermissionOpen, 
    onOpen: onPermissionOpen, 
    onClose: onPermissionClose 
  } = useDisclosure();

  const fetchPreviousInterviews = async () => {
    if (!applicationId || !hasPermission) return;
    
    setIsLoading(true);
    try {
      let endpoint = `api/recruitment/interviews/application/${applicationId}/previous`;
      const params = new URLSearchParams();
      
      // Add current interview exclusion
      if (currentInterviewId) {
        params.append('excludeInterview', currentInterviewId);
      }
      
      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }
      
      const response = await getApi(endpoint);
      setPreviousInterviews(response?.data?.data || []);
    } catch (error) {
      console.error('Error fetching previous interviews:', error);
      toast.error('Failed to load previous interview details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowPrevious = () => {
    if (!hasPermission) {
      onPermissionOpen();
      return;
    }
    setShowDetails(true);
    fetchPreviousInterviews();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    const date = new Date(timeString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'green';
      case 'scheduled': return 'blue';
      case 'cancelled': return 'red';
      case 'in_progress': return 'orange';
      default: return 'gray';
    }
  };

  const getRatingDisplay = (rating, label) => {
    if (!rating) return { stars: '-', color: 'gray.400', bgColor: 'gray.50' };
    
    const colors = {
      5: { color: 'green.500', bgColor: 'green.50' },
      4: { color: 'blue.500', bgColor: 'blue.50' },
      3: { color: 'orange.500', bgColor: 'orange.50' },
      2: { color: 'red.400', bgColor: 'red.50' },
      1: { color: 'red.500', bgColor: 'red.50' }
    };
    
    return {
      stars: '★'.repeat(rating) + '☆'.repeat(5 - rating),
      ...colors[rating] || colors[3]
    };
  };

  const decodeHtmlEntities = (text) => {
    if (!text) return '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  const stripHtmlTags = (html) => {
    if (!html) return '';
    const decodedHtml = decodeHtmlEntities(html);
    const div = document.createElement('div');
    div.innerHTML = decodedHtml;
    return div.textContent || div.innerText || '';
  };

  const getRecommendationDisplay = (action) => {
    const displays = {
      'hire': { label: 'Recommended for Hire', color: 'green', icon: CheckCircleIcon },
      'reject': { label: 'Not Recommended', color: 'red', icon: WarningIcon },
      'hold': { label: 'On Hold', color: 'orange', icon: TimeIcon },
      'maybe': { label: 'Maybe', color: 'yellow', icon: TimeIcon }
    };
    return displays[action?.toLowerCase()] || { label: action || 'Not Specified', color: 'gray', icon: TimeIcon };
  };

  useEffect(() => {
    if (isOpen && !showDetails) {
      setShowDetails(false);
      setPreviousInterviews([]);
    }
  }, [isOpen]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent maxH="90vh" mx={4}>
          <ModalHeader bg="blue.50" borderTopRadius="md">
            <VStack align="start" spacing={1}>
              <Text fontSize="lg" fontWeight="bold" color="blue.700">
                📋 Interview History & Feedback
              </Text>
              <Text fontSize="sm" fontWeight="normal" color="gray.600">
                {candidateName} • {jobTitle}
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={6}>
            {!showDetails ? (
              <VStack spacing={6} align="center" py={12}>
                <Box textAlign="center">
                  <Text fontSize="xl" fontWeight="semibold" mb={2}>
                    🔍 Review Previous Interview History
                  </Text>
                  <Text fontSize="md" color="gray.600" mb={4}>
                    Access detailed feedback and insights from previous interview rounds for this candidate.
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    This information will help you understand the candidate's journey and previous assessments.
                  </Text>
                </Box>
                <Button 
                  colorScheme="blue" 
                  onClick={handleShowPrevious}
                  size="lg"
                  leftIcon={<Icon as={CalendarIcon} />}
                >
                  View Interview History
                </Button>
              </VStack>
            ) : (
              <>
                {isLoading ? (
                  <Box textAlign="center" py={8}>
                    <Spinner size="lg" />
                    <Text mt={4}>Loading previous interviews...</Text>
                  </Box>
                ) : previousInterviews.length === 0 ? (
                  <Alert status="info">
                    <AlertIcon />
                    No previous interviews found for this application.
                  </Alert>
                ) : (
                  <VStack spacing={6} align="stretch">
                    <Box bg="blue.50" p={4} borderRadius="md" border="1px" borderColor="blue.200">
                      <HStack>
                        <Icon as={CalendarIcon} color="blue.500" />
                        <Text fontSize="md" fontWeight="semibold" color="blue.700">
                          Found {previousInterviews.length} previous interview{previousInterviews.length !== 1 ? 's' : ''} for this application
                        </Text>
                      </HStack>
                      <Text fontSize="sm" color="blue.600" mt={1}>
                        Review the candidate's interview journey and feedback from all previous rounds
                      </Text>
                    </Box>
                    
                    <VStack spacing={4} align="stretch">
                      {previousInterviews.map((interview, index) => {
                        const hasValidFeedback = interview.feedback && Object.keys(interview.feedback).length > 0;
                        
                        return (
                          <Card key={interview._id} variant="outline" shadow="sm">
                            <CardBody p={0}>
                              <Accordion allowToggle>
                                <AccordionItem border="none">
                                  <AccordionButton p={6} _hover={{ bg: 'gray.50' }}>
                                    <Box flex="1" textAlign="left">
                                      <Grid templateColumns="1fr auto auto" gap={4} w="full" alignItems="center">
                                        <VStack align="start" spacing={2}>
                                          <HStack spacing={3}>
                                            <Text fontSize="lg" fontWeight="bold" color="gray.800">
                                              🎯 {interview.round || 'Unknown Round'}
                                            </Text>
                                            <Badge 
                                              colorScheme={getStatusBadgeColor(interview.interviewStatus)}
                                              variant="solid"
                                              fontSize="xs"
                                              px={3}
                                              py={1}
                                              borderRadius="full"
                                            >
                                              {interview.interviewStatus?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
                                            </Badge>
                                            {hasValidFeedback && (
                                              <Badge colorScheme="green" variant="outline" fontSize="xs">
                                                ✓ Feedback Available
                                              </Badge>
                                            )}
                                          </HStack>
                                          <HStack spacing={4} color="gray.600">
                                            <HStack spacing={1}>
                                              <Icon as={CalendarIcon} boxSize={3} />
                                              <Text fontSize="sm">{formatDate(interview.interviewDate)}</Text>
                                            </HStack>
                                            <HStack spacing={1}>
                                              <Icon as={TimeIcon} boxSize={3} />
                                              <Text fontSize="sm">
                                                {formatTime(interview.startTime)} - {formatTime(interview.endTime)}
                                              </Text>
                                            </HStack>
                                          </HStack>
                                        </VStack>
                                        <AccordionIcon color="blue.500" boxSize={6} />
                                      </Grid>
                                    </Box>
                                  </AccordionButton>
                                  
                                  <AccordionPanel pb={6} px={6}>
                                    <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
                                      {/* Interview Details Column */}
                                      <GridItem>
                                        <Card bg="gray.50" variant="filled">
                                          <CardBody>
                                            <VStack align="stretch" spacing={4}>
                                              <HStack>
                                                <Icon as={CalendarIcon} color="blue.500" />
                                                <Text fontSize="md" fontWeight="bold" color="gray.800">
                                                  Interview Details
                                                </Text>
                                              </HStack>
                                              
                                              <VStack align="stretch" spacing={3}>
                                                <HStack justify="space-between">
                                                  <Text fontSize="sm" fontWeight="medium" color="gray.600">Interview ID:</Text>
                                                  <Text fontSize="sm" fontWeight="semibold">{interview.interviewId || '-'}</Text>
                                                </HStack>
                                                
                                                <HStack justify="space-between">
                                                  <Text fontSize="sm" fontWeight="medium" color="gray.600">Mode:</Text>
                                                  <Badge colorScheme="blue" variant="subtle">
                                                    {interview.interviewMode || '-'}
                                                  </Badge>
                                                </HStack>
                                                
                                                {interview.interviewers && interview.interviewers.length > 0 && (
                                                  <VStack align="stretch" spacing={2}>
                                                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Interviewers:</Text>
                                                    <VStack align="start" spacing={1} pl={2}>
                                                      {interview.interviewers.map((interviewer, idx) => (
                                                        <HStack key={idx} spacing={2}>
                                                          <Text fontSize="xs" color="blue.500">•</Text>
                                                          <Text fontSize="sm" fontWeight="medium">
                                                            {interviewer.panelName ? 
                                                              `${interviewer.panelName.firstName || ''} ${interviewer.panelName.lastName || ''}`.trim() 
                                                              : 'Unknown Interviewer'
                                                            }
                                                          </Text>
                                                        </HStack>
                                                      ))}
                                                    </VStack>
                                                  </VStack>
                                                )}
                                                
                                                {interview.reason && (
                                                  <VStack align="stretch" spacing={2}>
                                                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Reason/Notes:</Text>
                                                    <Box bg="white" p={3} borderRadius="md" border="1px" borderColor="gray.200">
                                                      <Text fontSize="sm">{stripHtmlTags(interview.reason)}</Text>
                                                    </Box>
                                                  </VStack>
                                                )}
                                                
                                                {interview.commentForInterviewer && (
                                                  <VStack align="stretch" spacing={2}>
                                                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Comments for Interviewer:</Text>
                                                    <Box bg="white" p={3} borderRadius="md" border="1px" borderColor="gray.200">
                                                      <Text fontSize="sm">{stripHtmlTags(interview.commentForInterviewer)}</Text>
                                                    </Box>
                                                  </VStack>
                                                )}
                                              </VStack>
                                            </VStack>
                                          </CardBody>
                                        </Card>
                                      </GridItem>
                                      
                                      {/* Feedback Details Column */}
                                      <GridItem>
                                        {hasValidFeedback ? (
                                          <Card bg="green.50" variant="filled" border="1px" borderColor="green.200">
                                            <CardBody>
                                              <VStack align="stretch" spacing={4}>
                                                <HStack>
                                                  <Icon as={StarIcon} color="green.500" />
                                                  <Text fontSize="md" fontWeight="bold" color="green.800">
                                                    Feedback & Assessment
                                                  </Text>
                                                </HStack>
                                                
                                                {/* Check if this is HR Round */}
                                                {interview.round === "HR Round" ? (
                                                  // HR Round - Show only Final Rating, Recommendation, and Comments
                                                  <VStack align="stretch" spacing={4}>
                                                    {/* Final Rating for HR Round */}
                                                    <Box bg="blue.50" p={4} borderRadius="md" textAlign="center">
                                                      <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                                                        Final Rating
                                                      </Text>
                                                      {(() => {
                                                        const rating = interview.feedback.rating;
                                                        const display = getRatingDisplay(rating, 'Final Rating');
                                                        return (
                                                          <>
                                                            <Text fontSize="2xl" color={display.color} fontWeight="bold">
                                                              {display.stars}
                                                            </Text>
                                                            <Text fontSize="sm" color={display.color} fontWeight="semibold">
                                                              {rating || 0}/5
                                                            </Text>
                                                          </>
                                                        );
                                                      })()
                                                      }
                                                    </Box>
                                                    
                                                    {/* Recommendation for HR Round */}
                                                    {interview.feedback.recommendedAction && (
                                                      <Box>
                                                        <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>Recommendation:</Text>
                                                        {(() => {
                                                          const rec = getRecommendationDisplay(interview.feedback.recommendedAction);
                                                          return (
                                                            <HStack spacing={2} bg="white" p={3} borderRadius="md" border="1px" borderColor="gray.200">
                                                              <Icon as={rec.icon} color={`${rec.color}.500`} />
                                                              <Badge colorScheme={rec.color} variant="solid" fontSize="sm" px={3} py={1}>
                                                                {rec.label}
                                                              </Badge>
                                                            </HStack>
                                                          );
                                                        })()
                                                        }
                                                      </Box>
                                                    )}
                                                    
                                                    {/* Comments for HR Round */}
                                                    {interview.feedback.comments && (
                                                      <VStack align="stretch" spacing={2}>
                                                        <Text fontSize="sm" fontWeight="medium" color="gray.600">HR Comments:</Text>
                                                        <Box 
                                                          bg="white" 
                                                          p={4} 
                                                          borderRadius="md" 
                                                          border="1px" 
                                                          borderColor="gray.200"
                                                          maxH="120px"
                                                          overflowY="auto"
                                                        >
                                                          <Text fontSize="sm" whiteSpace="pre-wrap" lineHeight="1.5">
                                                            {stripHtmlTags(interview.feedback.comments)}
                                                          </Text>
                                                        </Box>
                                                      </VStack>
                                                    )}
                                                  </VStack>
                                                ) : (
                                                  // Technical Round - Show all technical ratings
                                                  <VStack align="stretch" spacing={4}>
                                                    {/* Rating Summary for Technical Rounds */}
                                                    <Grid templateColumns="1fr 1fr" gap={3}>
                                                      {[
                                                        { key: 'rating', label: 'Final Rating' },
                                                        { key: 'technicalKnowledge', label: 'Technical' },
                                                        { key: 'communication', label: 'Communication' },
                                                        { key: 'problemSolvingSkills', label: 'Problem Solving' }
                                                      ].map(({ key, label }) => {
                                                        const rating = interview.feedback[key];
                                                        const display = getRatingDisplay(rating, label);
                                                        return (
                                                          <Box key={key} bg={display.bgColor} p={3} borderRadius="md" textAlign="center">
                                                            <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
                                                              {label}
                                                            </Text>
                                                            <Text fontSize="lg" color={display.color} fontWeight="bold">
                                                              {display.stars}
                                                            </Text>
                                                            <Text fontSize="xs" color={display.color} fontWeight="semibold">
                                                              {rating || 0}/5
                                                            </Text>
                                                          </Box>
                                                        );
                                                      })}
                                                    </Grid>
                                                    
                                                    {/* Recommendation for Technical Rounds */}
                                                    {interview.feedback.recommendedAction && (
                                                      <Box>
                                                        <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>Recommendation:</Text>
                                                        {(() => {
                                                          const rec = getRecommendationDisplay(interview.feedback.recommendedAction);
                                                          return (
                                                            <HStack spacing={2} bg="white" p={3} borderRadius="md" border="1px" borderColor="gray.200">
                                                              <Icon as={rec.icon} color={`${rec.color}.500`} />
                                                              <Badge colorScheme={rec.color} variant="solid" fontSize="sm" px={3} py={1}>
                                                                {rec.label}
                                                              </Badge>
                                                            </HStack>
                                                          );
                                                        })()
                                                        }
                                                      </Box>
                                                    )}
                                                    
                                                    {/* Technical Details */}
                                                    {(interview.feedback.technicalKnowledgeDetails || interview.feedback.problemSolvingDetails) && (
                                                      <VStack align="stretch" spacing={3}>
                                                        <Text fontSize="sm" fontWeight="medium" color="gray.600">Technical Assessment:</Text>
                                                        
                                                        {interview.feedback.technicalKnowledgeDetails && (
                                                          <Box bg="white" p={3} borderRadius="md" border="1px" borderColor="gray.200">
                                                            <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>Technical Knowledge:</Text>
                                                            <Text fontSize="sm">{stripHtmlTags(interview.feedback.technicalKnowledgeDetails)}</Text>
                                                          </Box>
                                                        )}
                                                        
                                                        {interview.feedback.problemSolvingDetails && (
                                                          <Box bg="white" p={3} borderRadius="md" border="1px" borderColor="gray.200">
                                                            <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>Problem Solving:</Text>
                                                            <Text fontSize="sm">{stripHtmlTags(interview.feedback.problemSolvingDetails)}</Text>
                                                          </Box>
                                                        )}
                                                      </VStack>
                                                    )}
                                                    
                                                    {/* General Comments for Technical Rounds */}
                                                    {interview.feedback.comments && (
                                                      <VStack align="stretch" spacing={2}>
                                                        <Text fontSize="sm" fontWeight="medium" color="gray.600">Additional Comments:</Text>
                                                        <Box 
                                                          bg="white" 
                                                          p={4} 
                                                          borderRadius="md" 
                                                          border="1px" 
                                                          borderColor="gray.200"
                                                          maxH="120px"
                                                          overflowY="auto"
                                                        >
                                                          <Text fontSize="sm" whiteSpace="pre-wrap" lineHeight="1.5">
                                                            {stripHtmlTags(interview.feedback.comments)}
                                                          </Text>
                                                        </Box>
                                                      </VStack>
                                                    )}
                                                  </VStack>
                                                )}
                                                
                                                {/* Feedback Meta Information */}
                                                <Divider />
                                                <VStack align="stretch" spacing={2}>
                                                  {interview.feedback.createBy && (
                                                    <HStack justify="space-between">
                                                      <Text fontSize="xs" color="gray.500">Reviewed by:</Text>
                                                      <Text fontSize="xs" fontWeight="semibold">
                                                        {interview.feedback.createBy.firstName} {interview.feedback.createBy.lastName}
                                                      </Text>
                                                    </HStack>
                                                  )}
                                                  
                                                  {interview.feedback.createdAt && (
                                                    <HStack justify="space-between">
                                                      <Text fontSize="xs" color="gray.500">Submitted:</Text>
                                                      <Text fontSize="xs" fontWeight="semibold">
                                                        {formatDate(interview.feedback.createdAt)}
                                                      </Text>
                                                    </HStack>
                                                  )}
                                                </VStack>
                                              </VStack>
                                            </CardBody>
                                          </Card>
                                        ) : (
                                          <Card bg="orange.50" variant="filled" border="1px" borderColor="orange.200">
                                            <CardBody>
                                              <VStack spacing={3} align="center" py={8}>
                                                <Icon as={WarningIcon} color="orange.500" boxSize={8} />
                                                <Text fontSize="md" fontWeight="semibold" color="orange.800" textAlign="center">
                                                  No Feedback Available
                                                </Text>
                                                <Text fontSize="sm" color="orange.600" textAlign="center">
                                                  {interview.interviewStatus === 'completed' 
                                                    ? 'Feedback has not been submitted for this completed interview'
                                                    : 'Feedback will be available after the interview is completed'
                                                  }
                                                </Text>
                                              </VStack>
                                            </CardBody>
                                          </Card>
                                        )}
                                      </GridItem>
                                    </Grid>
                                  </AccordionPanel>
                                </AccordionItem>
                              </Accordion>
                            </CardBody>
                          </Card>
                        );
                      })}
                    </VStack>
                  </VStack>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter bg="gray.50" borderBottomRadius="md">
            <Button onClick={onClose} colorScheme="blue" variant="outline">
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Permission Request Modal */}
      <Modal isOpen={isPermissionOpen} onClose={onPermissionClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Permission Required</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="warning">
              <AlertIcon />
              <VStack align="start" spacing={2}>
                <Text fontWeight="bold">Access Restricted</Text>
                <Text fontSize="sm">
                  You don't have permission to view previous application interview details and feedbacks. 
                  Please contact your administrator to request access.
                </Text>
              </VStack>
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onPermissionClose}>Understood</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PreviousApplicationInterviewModal;